// 騎士の飛び込みの砂ぼこり — 見本の絵から作る2つの版 (2026-09-28)。
//   台帳 production.md「FXの作り方」: 見本の絵を画像生成し、その絵から切り出した材料をプログラムで動かして再現する。
//   見本 = そざい/fx/knight-lunge-dust/sheet_raw.png (6コマ・右へ弾ける土煙・生成時の透明度つき)。
//   材料 = public/sprites/fx/knight-lunge-dust/ (切り出し = scripts/knight_dust_material.py <sheet_raw.png> <出力先>)
//     frame_01〜06.png = 見本の6コマを本来の透明度で原寸切り出し。床 y=163・接点 x=4 でそろえた 426×169
//     chunk_1〜5.png   = 最大コマ (3コマ目) を横に5つの塊へ分けたもの (境目は揺らいだぼかし)。下端 = 床
//     pebble_1.png     = 見本の小石
//   貼る版 (painted) = 6コマをそのまま順に出す。コードの版 (code) = 塊を接点から時間差で押し出し、
//     上から先に・まだらにほどけさせ (材料を読み込む時に作る「ほどけ段階」)、床すれすれのもやを最後に残す。
//   色は見本の明るさを残したまま、騎士の広間の床より一段明るい青灰に染める。
// ブラウザ専用 (Image が無い Vitest では何もしない)。
import type { FxEvent } from './types'

type DustFx = Extract<FxEvent, { kind: 'knight_lunge_dust' }>

const BASE = '/sprites/fx/knight-lunge-dust/'
const FLOOR_Y = 163
const CONTACT_X = 4
/** 各塊の左端が、最大コマの中で接点から何 px 後ろにあったか (材料の px)。 */
const CHUNK_X = [0, 60, 136, 227, 311]
const TINT = [150, 156, 176] as const
/** ほどけ段階 (0 = 見本のまま → 3 = 床すれすれのもやだけ)。 */
const ERODE_LEVELS = [0, 0.35, 0.62, 0.9]
const LIFE = 0.5

let started = false
const frames: (HTMLCanvasElement | null)[] = new Array(6).fill(null)
const chunks: (HTMLCanvasElement[] | null)[] = new Array(5).fill(null)
let pebble: HTMLCanvasElement | null = null

const hash01 = (n: number): number => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }

/** なめらかな値ノイズ (0..1)。セルは約 22px。 */
function noise2(x: number, y: number, seed: number): number {
  const cx = Math.floor(x / 22), cy = Math.floor(y / 22)
  const fx = x / 22 - cx, fy = y / 22 - cy
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy)
  const v = (i: number, j: number) => hash01((cx + i) * 57.3 + (cy + j) * 91.1 + seed * 13.7)
  const a = v(0, 0) + (v(1, 0) - v(0, 0)) * sx
  const b = v(0, 1) + (v(1, 1) - v(0, 1)) * sx
  return a + (b - a) * sy
}

/** 染めて、必要ならほどけさせた材料を作る。erode>0 = 上ほど・ノイズの薄い所ほど先に消える。 */
function bake(img: HTMLImageElement, erode: number, seed: number): HTMLCanvasElement {
  const c = document.createElement('canvas')
  c.width = img.width; c.height = img.height
  const g = c.getContext('2d')!
  g.drawImage(img, 0, 0)
  const d = g.getImageData(0, 0, c.width, c.height)
  const p = d.data
  for (let y = 0; y < c.height; y++) {
    const height01 = Math.max(0, Math.min(1, (FLOOR_Y - y) / FLOOR_Y))
    for (let x = 0; x < c.width; x++) {
      const i = (y * c.width + x) * 4
      let a = p[i + 3] / 255
      if (a <= 0) continue
      const lum = (p[i] * 0.3 + p[i + 1] * 0.59 + p[i + 2] * 0.11) / 255
      if (erode > 0) {
        const n = 0.65 * noise2(x, y, seed) + 0.35 * noise2(x * 2.7, y * 2.7, seed + 5)
        const thr = erode * (0.45 + 0.9 * n) * (0.3 + 1.5 * height01)
        a *= Math.max(0, Math.min(1, (a - thr + 0.3) / 0.6))   // 境目を柔らかく (硬い欠けを作らない)
      }
      const shade = 0.55 + 0.6 * lum
      p[i] = Math.min(255, TINT[0] * shade)
      p[i + 1] = Math.min(255, TINT[1] * shade)
      p[i + 2] = Math.min(255, TINT[2] * shade)
      p[i + 3] = Math.round(a * 255)
    }
  }
  g.putImageData(d, 0, 0)
  return c
}

function load(name: string, done: (img: HTMLImageElement) => void): void {
  const img = new Image()
  img.onload = () => done(img)
  img.src = `${BASE}${name}`
}

/** 材料の読み込み開始 (render.ts の preload から)。 */
export function preloadKnightDustArt(): void {
  if (started || typeof Image === 'undefined' || typeof document === 'undefined') return
  started = true
  for (let i = 0; i < 6; i++) load(`frame_0${i + 1}.png`, (img) => { frames[i] = bake(img, 0, 0) })
  for (let i = 0; i < 5; i++) load(`chunk_${i + 1}.png`, (img) => { chunks[i] = ERODE_LEVELS.map((e) => bake(img, e, i + 1)) })
  load('pebble_1.png', (img) => { pebble = bake(img, 0, 0) })
}

/** 見本の大きさ → ゲームの px。最大コマ (幅 418) が量 1 で約 160px になる。 */
const scaleFor = (amount: number): number => 0.3 + 0.08 * amount

/** 後ろ (土煙が飛ぶ向き) が右なら 1。見本は右へ弾ける絵なので、左へ飛ぶ時だけ裏返す。 */
function begin(ctx: CanvasRenderingContext2D, f: DustFx, s: number): void {
  ctx.save()
  ctx.translate(f.x, f.y)
  ctx.scale(-f.dir * s, s)
  ctx.translate(-CONTACT_X, -FLOOR_Y)
}

// 貼る版: 見本の6コマの時刻 (秒)。最初に速く膨らみ、ゆっくりほどける。
const PAINTED_AT = [0, 0.04, 0.1, 0.19, 0.3, 0.41]

/** 貼る版。材料が未読み込みなら false (呼び出し側が前の描き方に戻す)。 */
export function drawKnightDustPainted(ctx: CanvasRenderingContext2D, f: DustFx, age: number): boolean {
  preloadKnightDustArt()
  if (frames.some((fr) => !fr)) return false
  if (age > LIFE) return true
  if (f.variant === 'scuff') return true   // 擦り跡は貼る版では出さない (見本が無い)
  let idx = 0
  for (let i = 0; i < PAINTED_AT.length; i++) if (age >= PAINTED_AT[i]) idx = i
  const tail = Math.max(0, (age - PAINTED_AT[5]) / (LIFE - PAINTED_AT[5]))
  ctx.globalAlpha = (0.75 + 0.25 * f.amount) * (1 - tail)
  begin(ctx, f, scaleFor(f.amount))
  ctx.drawImage(frames[idx]!, 0, 0)
  ctx.restore()
  ctx.globalAlpha = 1
  return true
}

// コードの版: 塊ごとの出る時刻・ほどけ始め (塊の順番をばらして、一斉に消えないように)。
// 見本の時刻: 0秒=接点に小さく濃い塊 / 0.04秒=7割まで伸びる / 0.1秒=最大 / 0.19秒=ちぎれ始め / 0.3秒=薄いもや / 0.41秒=名残。
const CHUNK_PUSH_S = [0.05, 0.06, 0.075, 0.09, 0.1]      // 接点に詰まった塊が伸び切るまで (先ほど遅い)
const CHUNK_ERODE_AT = [0.1, 0.08, 0.07, 0.06, 0.05]
// 消え始め→消え切り。先の塊 (渦の側) ほど早く消え、見本どおり後半は接点側の低いもやだけになる。
const CHUNK_FADE_AT = [0.2, 0.16, 0.14, 0.1, 0.08]
const CHUNK_FADE_END = [0.49, 0.42, 0.36, 0.3, 0.24]
const CHUNK_DRIFT = [4, 8, 12, 16, 20]      // 寿命の間に後ろへ流れる量 (材料の px)

function drawChunk(ctx: CanvasRenderingContext2D, levels: HTMLCanvasElement[], progress: number, x: number, alpha: number): void {
  // progress 0..3 = ほどけ段階。隣り合う2段を重ねてつなぐ (同じ絵のほどけ違いなので二重には見えない)。
  const lo = Math.min(levels.length - 1, Math.floor(progress))
  const hi = Math.min(levels.length - 1, lo + 1)
  const w = progress - lo
  if (alpha * (1 - w) > 0.003) { ctx.globalAlpha = alpha * (1 - w); ctx.drawImage(levels[lo], x, 0) }
  if (hi !== lo && alpha * w > 0.003) { ctx.globalAlpha = alpha * w; ctx.drawImage(levels[hi], x, 0) }
}

/** コードの版。材料が未読み込みなら false。 */
export function drawKnightDustCoded(ctx: CanvasRenderingContext2D, f: DustFx, age: number): boolean {
  preloadKnightDustArt()
  if (chunks.some((c) => !c)) return false
  if (age > LIFE) return true
  const s = scaleFor(f.amount)
  const seed = f.t * 91.7 + f.x
  if (f.variant === 'scuff') {
    // 擦り跡 = 滑った所に、床すれすれのもや (1コマ目の塊のいちばんほどけた段) を小さく低く。
    if (f.x2 === undefined || age > 0.3) return true
    const k = age / 0.3
    const mid = { ...f, x: (f.x + f.x2) / 2 - f.dir * 6 }
    ctx.globalAlpha = 0.5 * f.amount * (1 - k) * (1 - k)
    begin(ctx, mid, s * 0.55)
    ctx.drawImage(chunks[0]![2], -20 * k, 0)
    ctx.restore()
    ctx.globalAlpha = 1
    return true
  }
  const u = age / LIFE
  begin(ctx, f, 1)
  for (let c = 0; c < 5; c++) {
    const lt = age
    // 押し出し: 接点から後ろへ伸びる (先の塊ほど遅れて・遠くへ)。
    const push = 1 - (1 - Math.min(1, lt / CHUNK_PUSH_S[c])) ** 3
    const lift = 1 - (1 - Math.min(1, lt / 0.13)) ** 2   // 横に先に伸び、高さは少し遅れて立つ
    const sx = s * (0.5 + 0.5 * push + 0.1 * u)
    const sy = s * (0.45 + 0.55 * lift) * (1 - 0.3 * u)   // 後半は沈んで低くなる
    const x = (CHUNK_X[c] * (0.12 + 0.88 * push) + CHUNK_DRIFT[c] * u) * s
    // ほどけ: 塊ごとに時刻をずらし、上から・まだらに消え、最後は床すれすれのもや。
    const e = Math.max(0, (age - CHUNK_ERODE_AT[c]) / (LIFE - CHUNK_ERODE_AT[c]))
    const progress = Math.min(3, 3 * Math.min(1, e / 0.35))
    const fade = Math.max(0, Math.min(1, (age - CHUNK_FADE_AT[c]) / (CHUNK_FADE_END[c] - CHUNK_FADE_AT[c])))
    const alpha = (0.8 + 0.2 * f.amount) * (1 - fade * fade * (3 - 2 * fade)) * (0.7 + 0.3 * Math.min(1, lt / 0.02))
    ctx.save()
    ctx.translate(CONTACT_X + x, FLOOR_Y)
    ctx.scale(sx, sy)
    ctx.translate(-CONTACT_X, -FLOOR_Y)
    drawChunk(ctx, chunks[c]!, progress, CONTACT_X, alpha)
    ctx.restore()
  }
  // 小石: 見本の小石を2〜4粒、後ろ斜め上へ弾いて重力で落とす。
  if (pebble) {
    const n = 2 + Math.round(2 * f.amount)
    for (let i = 0; i < n; i++) {
      const h = hash01(seed + i * 7.3), h2 = hash01(seed + i * 2.9 + 11)
      const ang = 0.35 + h * 0.6
      const v = (150 + 150 * f.amount) * (0.55 + h2 * 0.6)
      const px = Math.cos(ang) * v * age
      const py = -Math.sin(ang) * v * age + 650 * age * age
      if (py > 1) continue
      const ps = (0.35 + 0.25 * h2) * s / 0.34
      ctx.globalAlpha = 0.95 * (1 - u)
      ctx.save()
      ctx.translate(CONTACT_X + px / 1, FLOOR_Y - 3 + py)
      ctx.scale(ps, ps)
      ctx.rotate(h * 6 + age * (4 + 6 * h2))
      ctx.drawImage(pebble, -pebble.width / 2, -pebble.height / 2)
      ctx.restore()
    }
  }
  ctx.restore()
  ctx.globalAlpha = 1
  return true
}
